import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "../styles/evento-detalle.module.css";
import notifStyles from "../../notificaciones/styles/notificaciones.module.css";

import { Tabs } from "../components/ui/Tabs";
import { fmtLongDate } from "../domain/eventoDetalleUtils";
import { useEventoDetalleData } from "../hooks/useEventoDetalleData";
import { buildAlertsForEvento } from "../domain/notificacionesEvento";

import { TabDatos } from "../components/detalles/TabDatos";
import { TabParticipantes } from "../components/detalles/TabParticipantes";
import { TabMenus } from "../components/detalles/TabMenus";
import { TabHabitaciones } from "../components/detalles/TabHabitaciones";
import { TabActividades } from "../components/detalles/TabActividades";
import { TabCacerias } from "../components/detalles/TabCacerias";
import { TabNotificaciones } from "../components/detalles/TabNotificaciones";
import { TabTransfer } from "../components/detalles/TabTransfer";

export default function EventoDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL;

    const [tab, setTab] = useState("datos");
    const { loading, notice, evento, catalogos } = useEventoDetalleData(id, apiUrl);

    const horizonDays = 180;

    const alerts = useMemo(() => {
        if (!evento) return [];
        return buildAlertsForEvento(evento, horizonDays);
    }, [evento, horizonDays]);

    const hasNotificaciones = alerts.length > 0;

    const participacionesOrdenadas = useMemo(() => {
        const parts = Array.isArray(evento?.participaciones) ? evento.participaciones : [];
        return [...parts].sort((a, b) => (a.orden ?? a.posicion ?? 0) - (b.orden ?? b.posicion ?? 0));
    }, [evento]);


    const tabs = useMemo(
        () => [
            { key: "datos", label: "Datos" },
            { key: "participantes", label: "Participantes" },
            { key: "transfer", label: "Transfer" },
            { key: "menus", label: "Menús" },
            { key: "habitaciones", label: "Habitaciones" },
            { key: "actividades", label: "Actividades" },
            { key: "cacerias", label: "Cacerías" },
            { key: "alertas", label: "Alertas", alert: hasNotificaciones },
        ],
        [hasNotificaciones]
    );

    const agentesLabel = useMemo(() => {
        if (!evento) return "—";
        if (Array.isArray(evento.agentes) && evento.agentes.length) {
            return evento.agentes.map((a) => `${a.nombre} ${a.apellidos}`).join(", ");
        }
        if (Array.isArray(evento.agenteIds) && evento.agenteIds.length) {
            return evento.agenteIds.join(", ");
        }
        return "—";
    }, [evento]);

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.wrap}>Cargando…</div>
            </div>
        );
    }

    if (notice) {
        return (
            <div className={styles.page}>
                <div className={styles.wrap}>
                    <div className={`${styles.alert} ${styles.error}`}>{notice}</div>
                    <button className={`${styles.btn} ${styles.ghost}`} onClick={() => navigate("/eventos")}>
                        Volver
                    </button>
                </div>
            </div>
        );
    }

    if (!evento) return null;

    return (
        <div className={styles.page}>
            <div className={`${styles.wrap} ${styles.stack}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                    <div>
                        <h2 style={{ margin: 0 }}>{evento.nombre || `Evento #${evento.id}`}</h2>
                        <div className={styles.muted}>
                            {fmtLongDate(evento.fechaInicio)} → {fmtLongDate(evento.fechaFin)}
                            {evento.esProvisional ? " • Provisional" : ""}
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 8 }}>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.primary}`}
                            onClick={() => navigate("/eventos", { state: { editEvento: evento } })}
                        >
                            Editar
                        </button>
                    </div>
                </div>

                <Tabs tabs={tabs} active={tab} onChange={setTab} />

                {tab === "datos" && <TabDatos evento={evento} agentesLabel={agentesLabel} />}
                {tab === "participantes" && <TabParticipantes evento={evento} catalogos={catalogos} participacionesOrdenadas={participacionesOrdenadas} />}
                {tab === "transfer" && <TabTransfer evento={evento} />}
                {tab === "menus" && <TabMenus evento={evento} />}
                {tab === "habitaciones" && <TabHabitaciones evento={evento} catalogos={catalogos} />}
                {tab === "actividades" && <TabActividades evento={evento} catalogos={catalogos} />}
                {tab === "cacerias" && <TabCacerias evento={evento} />}
                {tab === "alertas" && <TabNotificaciones evento={evento} alerts={alerts} styles={notifStyles} />}
            </div>
        </div>
    );
}